import {
  CameraOutlined, CheckCircleOutlined, ClockCircleOutlined, DeleteOutlined, DollarOutlined,
  FileSearchOutlined, PauseCircleOutlined, PlayCircleOutlined, PlusOutlined, PrinterOutlined, SafetyCertificateOutlined,
  SendOutlined, ToolOutlined, UploadOutlined, UserOutlined
} from "@ant-design/icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Alert, Button, Card, Descriptions, Empty, Input, InputNumber, List, Progress, Select,
  Space, Steps, Switch, Table, Tag, Typography, Upload
} from "antd";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { PermissionCode, type RepairOrderStatus } from "@autoflow/types";
import { useSearchParams } from "react-router-dom";
import {
  formatCurrency, formatDate, formatDateTime, formatGroupedInput, formatMileage, formatPhone, parseGroupedInput
} from "../../shared/utils/formatters";
import type { RepairOrderServiceItem, WorkflowData } from "./repair-order-types";
import { repairOrdersApi } from "./repair-orders-api";
import { customerName } from "../customers/customer-types";
import { useAuth } from "../auth/AuthProvider";

type TextMap = Record<string, string>;
type Props = { id: string; data: WorkflowData; text: TextMap; refresh: () => void };

function useWorkflowMutation<T>(action: (input: T) => Promise<unknown>, refresh: () => void) {
  return useMutation({ mutationFn: action, onSuccess: refresh });
}

async function printDocument(type: "estimate" | "workshop" | "settlement", title: string) {
  const sheet = document.querySelector<HTMLElement>(`.${type}-print-sheet`);
  if (!sheet) return;

  const frame = document.createElement("iframe");
  frame.title = title;
  frame.setAttribute("aria-hidden", "true");
  Object.assign(frame.style, {
    position: "fixed",
    left: "-10000px",
    top: "0",
    width: "816px",
    height: "1056px",
    border: "0"
  });
  document.body.appendChild(frame);

  const frameDocument = frame.contentDocument;
  const frameWindow = frame.contentWindow;
  if (!frameDocument || !frameWindow) {
    frame.remove();
    return;
  }

  const styles = Array.from(
    document.querySelectorAll<HTMLLinkElement | HTMLStyleElement>('link[rel="stylesheet"], style')
  ).map(node => node.outerHTML).join("");
  frameDocument.open();
  frameDocument.write(
    `<!doctype html><html><head><meta charset="UTF-8"><base href="${window.location.href}">${styles}</head>` +
    `<body data-print-document="${type}">${sheet.outerHTML}</body></html>`
  );
  frameDocument.close();
  frameDocument.title = title;

  const styleLinks = Array.from(frameDocument.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'));
  await Promise.all(styleLinks.map(link => {
    if (link.sheet) return Promise.resolve();
    return new Promise<void>(resolve => {
      link.addEventListener("load", () => resolve(), { once: true });
      link.addEventListener("error", () => resolve(), { once: true });
    });
  }));
  await frameDocument.fonts.ready;
  await new Promise<void>(resolve => frameWindow.requestAnimationFrame(() => {
    frameWindow.requestAnimationFrame(() => resolve());
  }));

  const cleanup = () => {
    if (frame.isConnected) frame.remove();
  };
  frameWindow.addEventListener("afterprint", cleanup, { once: true });
  frameWindow.focus();
  frameWindow.print();
  window.setTimeout(cleanup, 120_000);
}

export function WorkflowOverview({ id, data, text, refresh }: Props) {
  const order = data.repairOrder;
  const t = (key: string) => text[key] ?? key;
  const action = useWorkflowMutation(async (step: string) => {
    if (step === "check-in") return repairOrdersApi.checkIn(id);
    if (step === "start-work") return repairOrdersApi.startWork(id);
    if (step === "quality") return repairOrdersApi.qualityCheck(id);
    if (step === "ready") return repairOrdersApi.readyDelivery(id);
    return repairOrdersApi.close(id);
  }, refresh);
  const next = nextAction(order.status, data, text);
  const progressCount = Object.values(data.progress).filter(Boolean).length;
  return <div className="workflow-section-stack">
    {action.isError && <MutationError error={action.error} />}
    <Card title={text.workflowProgress}>
      <Progress percent={Math.round((progressCount / Object.keys(data.progress).length) * 100)} />
      <Steps
        size="small"
        responsive
        current={workflowStep(order.status)}
        items={[
          text.checkedIn, text.preInspectionEstimate, text.approve,
          text.labor, text.issueParts, text.invoice, text.payment, text.closed
        ].map(title => ({ title }))}
      />
    </Card>
    <div className="workflow-overview-grid">
      <Card title={text.repairInformation}><Descriptions size="small" column={1} items={[
        { key: "status", label: text.status, children: order.status.replaceAll("_", " ") },
        { key: "concern", label: text.customerConcern, children: order.customerConcern || "—" },
        { key: "mileage", label: text.mileage, children: formatMileage(order.mileageIn) },
        { key: "warranty", label: text.warranty, children: order.warranty ? text.yes : text.no }
      ]} /></Card>
      <Card title={text.nextWorkflowAction}>
        {next ? <><Typography.Paragraph>{next.description}</Typography.Paragraph><Button type="primary" size="large" loading={action.isPending} onClick={() => action.mutate(next.key)}>{next.label}</Button></> : <Alert type="success" showIcon message={text.repairCompleted} />}
      </Card>
    </div>
    <div className="workflow-summary-strip">
      <SummaryTile label={t("preInspectionEstimate")} value={data.estimate
        ? `${order.inspection?.items.length ?? 0} ${t("findings")} · ${formatCurrency(data.estimate.total)}`
        : order.inspection ? `${order.inspection.items.length} ${t("findings")}` : t("notStarted")} />
      <SummaryTile label={t("labor")} value={`${order.laborEntries.filter(item => item.status === "COMPLETED").length}/${order.laborEntries.length}`} />
      <SummaryTile label={t("parts")} value={`${order.parts.reduce((sum, item) => sum + item.issuedQuantity, 0)}/${order.parts.reduce((sum, item) => sum + item.requiredQuantity, 0)}`} />
      <SummaryTile label={t("payment")} value={order.invoice ? formatCurrency(order.invoice.balance) : t("notInvoiced")} />
    </div>
  </div>;
}

export function InspectionSection({ id, data, text, refresh }: Props) {
  const [name, setName] = useState("");
  const [condition, setCondition] = useState("PASS");
  const [note, setNote] = useState("");
  const add = useWorkflowMutation<void>(() => repairOrdersApi.addInspectionItem(id, { name, condition, note }), () => {
    setName(""); setNote(""); refresh();
  });
  const remove = useWorkflowMutation((itemId: string) => repairOrdersApi.removeInspectionItem(id, itemId), refresh);
  const complete = useWorkflowMutation<void>(() => repairOrdersApi.completeInspection(id), refresh);
  const inspection = data.repairOrder.inspection;
  const editable = ["CHECKED_IN", "DIAGNOSIS"].includes(data.repairOrder.status);
  return <div className="workflow-section-stack">
    {(add.isError || complete.isError || remove.isError) && <MutationError error={add.error || complete.error || remove.error} />}
    <Card title={text.addInspectionFinding}>
      <form onSubmit={event => { event.preventDefault(); if (editable && name.trim()) add.mutate(); }}>
      <Space wrap className="workflow-inline-form">
        <Input value={name} onChange={event => setName(event.target.value)} placeholder={text.inspectionItem} disabled={!editable} />
        <Select value={condition} onChange={setCondition} disabled={!editable} options={[
          { value: "PASS", label: text.normal }, { value: "FAIL", label: text.abnormal }
        ]} />
        <Input value={note} onChange={event => setNote(event.target.value)} placeholder={text.repairRecommendation} disabled={!editable} />
        <Button type="primary" htmlType="submit" disabled={!editable || !name.trim()} loading={add.isPending}>{text.add}</Button>
      </Space>
      </form>
    </Card>
    <Card title={`${text.inspection} (${inspection?.items.length ?? 0})`} extra={editable && <Button type="primary" icon={<CheckCircleOutlined />} disabled={!inspection?.items.length} loading={complete.isPending} onClick={() => complete.mutate()}>{text.completeInspection}</Button>}>
      <List
        dataSource={inspection?.items ?? []}
        locale={{ emptyText: text.noData }}
        renderItem={(item, index) => <List.Item actions={editable ? [<Button key="delete" danger type="link" icon={<DeleteOutlined />} onClick={() => remove.mutate(item.id)}>{text.delete}</Button>] : []}>
          <List.Item.Meta title={<Space><b>{index + 1}.</b>{item.name}<FindingTag condition={item.condition} text={text} /></Space>} description={item.note || "—"} />
        </List.Item>}
      />
    </Card>
  </div>;
}

export function PreInspectionEstimateSection(props: Props) {
  return <div className="preinspection-estimate-workspace">
    <InspectionSection {...props} />
    <EstimateSection {...props} />
  </div>;
}

export function SupplementApprovalPanel({ id, data, text, refresh }: Props) {
  const t = (key: string) => text[key] ?? key;
  const [approvedBy, setApprovedBy] = useState("");
  const [approvalMethod, setApprovalMethod] = useState<"TEXT" | "IMAGE" | "PHONE_RECORDING" | "SIGNED_ESTIMATE">("TEXT");
  const [evidence, setEvidence] = useState("");
  const approve = useWorkflowMutation<void>(() => repairOrdersApi.approveSupplement(id, {
    approvedByName: approvedBy,
    approvalMethod,
    approvalEvidence: evidence.trim() || undefined
  }), () => {
    setApprovedBy("");
    setEvidence("");
    refresh();
  });
  const uploadConfirmation = useWorkflowMutation(
    (file: File) => repairOrdersApi.uploadEstimateAttachment(id, file),
    refresh
  );

  if (data.repairOrder.status !== "SUPPLEMENT_REQUIRED") return null;
  const pendingServices = data.repairOrder.services.filter(service => !service.approved);

  return <Card
    className="supplement-approval-panel"
    title={t("supplementRequired")}
    extra={<Tag color="orange">{t("attentionWAITING_CUSTOMER_APPROVAL")}</Tag>}
  >
    {(approve.isError || uploadConfirmation.isError)
      && <MutationError error={approve.error || uploadConfirmation.error} />}
    <Typography.Paragraph>{t("supplementRequiredHint")}</Typography.Paragraph>
    <div className="customer-decision-summary">
      <div>
        <small>{t("supplementChangedScope")}</small>
        <b>{pendingServices.length
          ? pendingServices.map(service => service.serviceCatalog.serviceName).join(" · ")
          : t("supplementEstimateChanges")}</b>
      </div>
      <div>
        <small>{t("supplementTotal")}</small>
        <b>{formatCurrency(data.estimate?.total ?? 0)}</b>
      </div>
      <div>
        <small>{t("confirmationAttachments")}</small>
        <b>{data.estimate?.approvalAttachments.length ?? 0}</b>
      </div>
    </div>
    <form className="supplement-approval-form" onSubmit={event => {
      event.preventDefault();
      if (approvedBy.trim()) approve.mutate();
    }}>
      <Input
        value={approvedBy}
        onChange={event => setApprovedBy(event.target.value)}
        placeholder={t("customerConfirmationName")}
      />
      <Select value={approvalMethod} onChange={setApprovalMethod} options={[
        { value: "TEXT", label: t("confirmationText") },
        { value: "IMAGE", label: t("confirmationImage") },
        { value: "PHONE_RECORDING", label: t("confirmationRecording") },
        { value: "SIGNED_ESTIMATE", label: t("confirmationSignedEstimate") }
      ]} />
      <Input
        value={evidence}
        onChange={event => setEvidence(event.target.value)}
        placeholder={t("confirmationEvidence")}
      />
      <div className="supplement-approval-actions">
        <Upload
          showUploadList={false}
          accept=".jpg,.jpeg,.png,.pdf,.mp3,.m4a,.wav"
          beforeUpload={file => {
            uploadConfirmation.mutate(file);
            return false;
          }}
        >
          <Button icon={<UploadOutlined />} loading={uploadConfirmation.isPending}>
            {t("uploadAttachment")}
          </Button>
        </Upload>
        <Button
          type="primary"
          htmlType="submit"
          disabled={!approvedBy.trim()}
          loading={approve.isPending}
        >{t("approveSupplement")}</Button>
      </div>
    </form>
  </Card>;
}

export function EstimateSection({ id, data, text, refresh }: Props) {
  const [searchParams] = useSearchParams();
  const [type, setType] = useState("LABOR");
  const [description, setDescription] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [unitPrice, setUnitPrice] = useState(0);
  const [partId, setPartId] = useState<string>();
  const [estimateMaterialSource, setEstimateMaterialSource] = useState<"CATALOG" | "CUSTOM">("CATALOG");
  const [approvedBy, setApprovedBy] = useState("");
  const [approvalMethod, setApprovalMethod] = useState<"TEXT" | "IMAGE" | "PHONE_RECORDING" | "SIGNED_ESTIMATE">("TEXT");
  const [approvalEvidence, setApprovalEvidence] = useState("");
  const estimate = data.estimate;
  const editable = data.repairOrder.status === "ESTIMATE";
  const add = useWorkflowMutation<void>(() => repairOrdersApi.addEstimateLine(id, {
    type, description, quantity, unitPrice,
    ...(type === "PART" && estimateMaterialSource === "CATALOG" && partId ? { partId } : {})
  }), () => { setDescription(""); refresh(); });
  const remove = useWorkflowMutation((lineId: string) => repairOrdersApi.removeEstimateLine(id, lineId), refresh);
  const submit = useWorkflowMutation<void>(() => repairOrdersApi.submitEstimate(id, 0.0975, {
    shopSupplyAmount: Number(estimate?.shopSupplyAmount ?? 0),
    discountAmount: Number(estimate?.discountAmount ?? 0)
  }), refresh);
  const approve = useWorkflowMutation<void>(() => repairOrdersApi.approveEstimate(id, {
    approvedByName: approvedBy,
    approvalMethod,
    approvalEvidence: approvalEvidence.trim() || undefined
  }), refresh);
  const uploadConfirmation = useWorkflowMutation(
    (file: File) => repairOrdersApi.uploadEstimateAttachment(id, file),
    refresh
  );
  const laborTotal = estimate?.lines.filter(line => line.type === "LABOR").reduce((sum, line) => sum + Number(line.lineTotal), 0) ?? 0;
  const materialTotal = estimate?.lines.filter(line => line.type === "PART").reduce((sum, line) => sum + Number(line.lineTotal), 0) ?? 0;
  const calculatedAmount = quantity * unitPrice;
  const performEstimatePrint = useCallback(
    () => {
      void printDocument("estimate", `报价单-${data.repairOrder.roNumber}`);
    },
    [data.repairOrder.roNumber]
  );
  const printEstimate = useCallback(
    () => {
      void repairOrdersApi.logDocumentPrint(id, "ESTIMATE").finally(() => {
        performEstimatePrint();
      });
    },
    [id, performEstimatePrint]
  );
  useEffect(() => {
    if (searchParams.get("print") === "estimate") performEstimatePrint();
  }, [performEstimatePrint, searchParams]);
  const choosePart = (value: string) => {
    const part = data.availableParts.find(item => item.id === value);
    setPartId(value);
    if (part) { setDescription(part.name); setUnitPrice(Number(part.unitPrice)); }
  };
  return <div className="workflow-section-stack">
    {(add.isError || remove.isError || submit.isError || approve.isError || uploadConfirmation.isError) && <MutationError error={add.error || remove.error || submit.error || approve.error || uploadConfirmation.error} />}
    {editable && <Card title={text.addEstimateItem}><form onSubmit={event => { event.preventDefault(); if (description.trim() && (type !== "PART" || estimateMaterialSource === "CUSTOM" || partId)) add.mutate(); }}>
      <Space wrap className="workflow-inline-form estimate-form">
        <Select value={type} onChange={setType} options={[
          { value: "LABOR", label: text.laborCost },
          { value: "PART", label: text.materialCost }
        ]} />
        {type === "PART" && <Select value={estimateMaterialSource} onChange={value => {
          setEstimateMaterialSource(value);
          if (value === "CUSTOM") {
            setPartId(undefined);
            setDescription("");
            setUnitPrice(0);
          }
        }} options={[
          { value: "CATALOG", label: text.inventoryMaterial },
          { value: "CUSTOM", label: text.freeMaterial }
        ]} />}
        {type === "PART" && estimateMaterialSource === "CATALOG" && <Select showSearch optionFilterProp="label" value={partId} onChange={choosePart} placeholder={text.selectPart} options={data.availableParts.map(part => ({ value: part.id, label: `${part.sku} · ${part.name} (${part.onHand})` }))} />}
        <Input value={description} onChange={event => setDescription(event.target.value)} placeholder={text.description} />
        <InputNumber
          min={type === "LABOR" ? 0.1 : 1}
          step={type === "LABOR" ? 0.1 : 1}
          precision={type === "PART" ? 0 : 2}
          value={quantity}
          onChange={value => setQuantity(value ?? 1)}
          addonBefore={type === "LABOR" ? text.hours : text.quantity}
        />
        <InputNumber min={0} precision={2} value={unitPrice} formatter={formatGroupedInput} parser={parseGroupedInput} onChange={value => setUnitPrice(value ?? 0)} addonBefore={`${text.unitPrice} $`} />
        <InputNumber readOnly controls={false} precision={2} value={calculatedAmount} formatter={formatGroupedInput} addonBefore={`${text.amount} $`} />
        <Button type="primary" htmlType="submit" disabled={!description.trim() || (type === "PART" && estimateMaterialSource === "CATALOG" && !partId)} loading={add.isPending}>{text.add}</Button>
      </Space>
    </form></Card>}
    <div className="estimate-category-summary">
      <SummaryTile label={text.laborCost ?? "Labor"} value={formatCurrency(laborTotal)} />
      <SummaryTile label={text.materialCost ?? "Materials"} value={formatCurrency(materialTotal)} />
      <SummaryTile label={`${text.tax} (9.75%)`} value={formatCurrency(estimate?.taxAmount ?? materialTotal * 0.0975)} />
      <SummaryTile label={text.grandTotal ?? "Grand total"} value={formatCurrency(estimate?.total ?? 0)} />
    </div>
    <Card title={text.estimate} extra={<EstimateStatus status={estimate?.status} />}>
      <Table rowKey="id" pagination={false} dataSource={estimate?.lines ?? []} columns={[
        { title: text.sequence, key: "sequence", width: 68, render: (_: unknown, __: unknown, index: number) => index + 1 },
        { title: text.type, dataIndex: "type" },
        { title: text.repairItem, dataIndex: "description" },
        { title: text.quantity, dataIndex: "quantity", align: "right" as const },
        { title: text.unitPrice, dataIndex: "unitPrice", align: "right" as const, render: formatCurrency },
        { title: text.total, dataIndex: "lineTotal", align: "right" as const, render: formatCurrency },
        ...(editable ? [{ title: "", key: "actions", render: (_: unknown, line: { id: string }) => <Button danger type="link" onClick={() => remove.mutate(line.id)}>{text.delete}</Button> }] : [])
      ]} />
      <div className="estimate-totals">
        <span>{text.subtotal}<b>{formatCurrency(estimate?.subtotal ?? 0)}</b></span>
        <span>{text.taxableMaterials}<b>{formatCurrency(materialTotal)}</b></span>
        <span>{text.losAngelesSalesTax}<b>{formatCurrency(estimate?.taxAmount ?? materialTotal * 0.0975)}</b></span>
        <span className="grand-total">{text.total}<b>{formatCurrency(estimate?.total ?? 0)}</b></span>
      </div>
      {editable && <Space wrap>
        <Button type="primary" icon={<SendOutlined />} disabled={!estimate?.lines.length} loading={submit.isPending} onClick={() => submit.mutate()}>{text.submitForApproval}</Button>
        <Button icon={<PrinterOutlined />} disabled={!estimate?.lines.length} onClick={printEstimate}>{text.printEstimatePdf}</Button>
      </Space>}
      {data.repairOrder.status === "WAITING_APPROVAL" && <form className="customer-confirmation-form" onSubmit={event => { event.preventDefault(); if (approvedBy.trim()) approve.mutate(); }}>
        <Space wrap className="approval-bar">
          <Input value={approvedBy} onChange={event => setApprovedBy(event.target.value)} placeholder={text.customerConfirmationName} />
          <Select value={approvalMethod} onChange={setApprovalMethod} options={[
            { value: "TEXT", label: text.confirmationText },
            { value: "IMAGE", label: text.confirmationImage },
            { value: "PHONE_RECORDING", label: text.confirmationRecording },
            { value: "SIGNED_ESTIMATE", label: text.confirmationSignedEstimate }
          ]} />
          <Input value={approvalEvidence} onChange={event => setApprovalEvidence(event.target.value)} placeholder={text.confirmationEvidence} />
          <Button icon={<PrinterOutlined />} onClick={printEstimate}>{text.printEstimatePdf}</Button>
          <Button type="primary" htmlType="submit" disabled={!approvedBy.trim()} loading={approve.isPending}>{text.confirmWithCustomer}</Button>
          <Upload
            showUploadList={false}
            accept=".jpg,.jpeg,.png,.pdf,.mp3,.m4a,.wav"
            beforeUpload={file => { uploadConfirmation.mutate(file); return false; }}
          >
            <Button icon={<UploadOutlined />} loading={uploadConfirmation.isPending}>{text.uploadAttachment}</Button>
          </Upload>
        </Space>
      </form>}
      {estimate?.status === "APPROVED" && <Space direction="vertical" style={{ width: "100%" }}>
        <Alert type="success" showIcon message={`${text.customerConfirmedBy}: ${estimate.approvedByName} · ${text[estimate.approvalMethod?.toLowerCase() ?? ""] ?? estimate.approvalMethod ?? ""}`} />
        <Space wrap>
          <Button icon={<PrinterOutlined />} onClick={printEstimate}>{text.printEstimatePdf}</Button>
          <Upload
            showUploadList={false}
            accept=".jpg,.jpeg,.png,.pdf,.mp3,.m4a,.wav"
            beforeUpload={file => { uploadConfirmation.mutate(file); return false; }}
          >
            <Button icon={<UploadOutlined />} loading={uploadConfirmation.isPending}>{text.uploadAttachment}</Button>
          </Upload>
        </Space>
      </Space>}
      {!!estimate?.approvalAttachments.length && <List
        size="small"
        header={text.confirmationAttachments}
        dataSource={estimate.approvalAttachments}
        renderItem={item => <List.Item><List.Item.Meta title={item.fileName} description={`${item.mimeType} · ${(item.size / 1024).toFixed(1)} KB · ${formatDateTime(item.createdAt)}`} /></List.Item>}
      />}
    </Card>
    <EstimatePrintSheet data={data} text={text} laborTotal={laborTotal} materialTotal={materialTotal} />
  </div>;
}

function EstimatePrintSheet({ data, text, laborTotal, materialTotal }: {
  data: WorkflowData;
  text: TextMap;
  laborTotal: number;
  materialTotal: number;
}) {
  const order = data.repairOrder;
  const estimate = data.estimate;
  const laborLines = estimate?.lines.filter(line => line.type === "LABOR") ?? [];
  const materialLines = estimate?.lines.filter(line => line.type === "PART") ?? [];
  const taxTotal = Number(estimate?.taxAmount ?? materialTotal * 0.0975);
  const grandTotal = Number(estimate?.total ?? laborTotal + materialTotal + taxTotal);
  return createPortal(<section className="print-document estimate-print-sheet" aria-hidden="true">
    <BilingualDocumentHeader titleEn="ESTIMATE" titleZh="估价单" documentNumber={order.roNumber} order={order} />
    <DocumentConcern order={order} />
    <DocumentSectionTitle en="PRE-INSPECTION" zh="预检" />
    <table className="repair-document-table">
      <thead><tr><th>序号<br />NO.</th><th>预检项目<br />INSPECTION ITEM</th><th>状态<br />CONDITION</th><th>维修建议<br />RECOMMENDATION</th></tr></thead>
      <tbody>{order.inspection?.items.length ? order.inspection.items.map((item, index) => <tr key={item.id}><td>{index + 1}</td><td>{item.name}</td><td>{text[item.condition.toLowerCase()]}</td><td>{item.note || "—"}</td></tr>) : <tr><td colSpan={4}>—</td></tr>}</tbody>
    </table>
    <DocumentSectionTitle en="LABOR" zh="人工" />
    <LaborDocumentTable lines={laborLines.map(line => ({ id: line.id, description: line.description, hours: line.quantity, rate: line.unitPrice, total: line.lineTotal }))} />
    <DocumentSectionTitle en="PARTS / MATERIALS" zh="零件 / 材料" />
    <MaterialDocumentTable lines={materialLines.map(line => ({ id: line.id, sku: "—", name: line.description, quantity: line.quantity, unitPrice: line.unitPrice, total: line.lineTotal }))} />
    <div className="repair-document-lower">
      <DocumentNotes notes={order.internalNotes} />
      <DocumentTotals labor={laborTotal} materials={materialTotal} discount={Number(estimate?.discountAmount ?? 0)} tax={taxTotal} total={grandTotal} />
    </div>
    <BilingualDocumentFooter />
  </section>, document.body);
}

export function ServiceIntelligencePanel({ id, data, text, refresh }: Props) {
  const { user } = useAuth();
  const t = (key: string) => text[key] ?? key;
  const canRunLabor = user?.permissions.includes(PermissionCode.LaborWrite) ?? false;
  const canIssueParts = user?.permissions.includes(PermissionCode.InventoryIssue) ?? false;
  const canPerformQc = user?.permissions.includes(PermissionCode.RepairOrderWrite) ?? false;
  const editable = ["ESTIMATE", "IN_PROGRESS", "SUPPLEMENT_REQUIRED"].includes(data.repairOrder.status);
  const [query, setQuery] = useState("");
  const [selectedCatalogId, setSelectedCatalogId] = useState<string>();
  const [overrideServiceId, setOverrideServiceId] = useState<string>();
  const [finalLaborTime, setFinalLaborTime] = useState(0);
  const [overrideLaborRate, setOverrideLaborRate] = useState(0);
  const [overrideReason, setOverrideReason] = useState("");
  const [partServiceId, setPartServiceId] = useState<string>();
  const [partNumber, setPartNumber] = useState("");
  const [partDescription, setPartDescription] = useState("");
  const [partQty, setPartQty] = useState(1);
  const [partPrice, setPartPrice] = useState(0);
  const [partSupplier, setPartSupplier] = useState("");
  const [evidenceServiceId, setEvidenceServiceId] = useState<string>();
  const [evidenceType, setEvidenceType] = useState<RepairOrderServiceItem["evidence"][number]["type"]>("PHOTO");
  const [evidenceSummary, setEvidenceSummary] = useState("");
  const [evidenceValue, setEvidenceValue] = useState("");
  const [evidenceUnit, setEvidenceUnit] = useState("");
  const [evidenceFile, setEvidenceFile] = useState<File>();
  const [evidenceCustomerVisible, setEvidenceCustomerVisible] = useState(true);
  const catalog = useQuery({
    queryKey: ["repair-order-service-catalog", id, query],
    queryFn: () => repairOrdersApi.searchServiceCatalog(id, query),
    enabled: editable,
    staleTime: 30_000
  });
  const add = useWorkflowMutation<void>(() => repairOrdersApi.addService(id, {
    serviceCatalogId: selectedCatalogId!
  }), () => {
    setSelectedCatalogId(undefined);
    setQuery("");
    refresh();
  });
  const remove = useWorkflowMutation((serviceId: string) => repairOrdersApi.removeService(id, serviceId), refresh);
  const override = useWorkflowMutation<void>(() => repairOrdersApi.overrideServiceLabor(
    id,
    overrideServiceId!,
    {
      finalLaborTime,
      laborRate: overrideLaborRate,
      overrideReason
    }
  ), () => {
    setOverrideServiceId(undefined);
    setOverrideReason("");
    refresh();
  });
  const addPart = useWorkflowMutation<void>(() => repairOrdersApi.addServicePart(
    id,
    partServiceId!,
    {
      partSource: "MANUAL",
      partNumber: partNumber.trim(),
      description: partDescription.trim(),
      qty: partQty,
      unitPrice: partPrice,
      supplier: partSupplier.trim() || undefined
    }
  ), () => {
    setPartServiceId(undefined);
    setPartNumber("");
    setPartDescription("");
    setPartQty(1);
    setPartPrice(0);
    setPartSupplier("");
    refresh();
  });
  const addEvidence = useWorkflowMutation<void>(() => repairOrdersApi.addServiceEvidence(
    id,
    evidenceServiceId!,
    {
      type: evidenceType,
      summary: evidenceSummary.trim() || undefined,
      value: evidenceValue.trim() || undefined,
      unit: evidenceUnit.trim() || undefined,
      customerVisible: evidenceCustomerVisible,
      file: evidenceFile
    }
  ), () => {
    setEvidenceServiceId(undefined);
    setEvidenceSummary("");
    setEvidenceValue("");
    setEvidenceUnit("");
    setEvidenceFile(undefined);
    setEvidenceCustomerVisible(true);
    refresh();
  });
  const startService = useWorkflowMutation(
    (service: RepairOrderServiceItem) => repairOrdersApi.startServiceWork(id, service.id),
    refresh
  );
  const pauseService = useWorkflowMutation(
    (service: RepairOrderServiceItem) => repairOrdersApi.pauseServiceWork(id, service.id),
    refresh
  );
  const resumeService = useWorkflowMutation(
    (service: RepairOrderServiceItem) => repairOrdersApi.resumeServiceWork(id, service.id),
    refresh
  );
  const completeService = useWorkflowMutation(
    (service: RepairOrderServiceItem) => repairOrdersApi.completeServiceWork(id, service.id),
    refresh
  );
  const issueServicePart = useWorkflowMutation(
    (input: { serviceId: string; partId: string; quantity: number }) =>
      repairOrdersApi.issueServicePart(id, input.serviceId, input.partId, input.quantity),
    refresh
  );
  const serviceQc = useWorkflowMutation(
    (input: { serviceId: string; passed: boolean }) =>
      repairOrdersApi.serviceQc(id, input.serviceId, input.passed),
    refresh
  );
  const assignTechnician = useWorkflowMutation(
    (input: { serviceId: string; technicianId: string }) =>
      repairOrdersApi.assignServiceTechnician(id, input.serviceId, input.technicianId),
    refresh
  );
  const error = add.error || remove.error || override.error || addPart.error || addEvidence.error
    || startService.error || pauseService.error || resumeService.error || completeService.error
    || issueServicePart.error || serviceQc.error || assignTechnician.error || catalog.error;
  const openOverride = (service: RepairOrderServiceItem) => {
    setOverrideServiceId(service.id);
    setFinalLaborTime(Number(service.finalLaborTime));
    setOverrideLaborRate(Number(service.laborRate));
    setOverrideReason("");
  };

  return <Card
    className="service-intelligence-panel"
    title={<Space><ToolOutlined /><span>{t("serviceIntelligence")}</span></Space>}
    extra={<Tag color="blue">{t("workflowFirst")}</Tag>}
  >
    {error && <MutationError error={error} />}
    <Typography.Paragraph type="secondary">{t("serviceIntelligenceHint")}</Typography.Paragraph>
    <div className="add-service-bar">
      <Select
        showSearch
        allowClear
        filterOption={false}
        value={selectedCatalogId}
        searchValue={query}
        onSearch={setQuery}
        onChange={setSelectedCatalogId}
        placeholder={t("searchMatchedService")}
        loading={catalog.isFetching}
        disabled={!editable}
        options={(catalog.data ?? []).map(item => ({
          value: item.id,
          label: `${item.serviceName} · ${item.defaultLaborGuide?.shopDefaultTime ?? "—"} ${t("hours")}`
        }))}
      />
      <Button
        type="primary"
        icon={<PlusOutlined />}
        disabled={!editable || !selectedCatalogId}
        loading={add.isPending}
        onClick={() => add.mutate()}
      >{t("addService")}</Button>
    </div>
    {!editable && <Alert type="info" showIcon message={t("serviceChangeStatusHint")} />}
    <div className="service-card-list">
      {data.repairOrder.services.length === 0
        ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("noServices")} />
        : data.repairOrder.services.map((service, index) => <Card
          key={service.id}
          size="small"
          className="repair-service-card"
          title={<div className="service-card-title">
            <Typography.Text type="secondary">{t("service")} #{index + 1} · {service.serviceCatalog.serviceCode}</Typography.Text>
            <Typography.Title level={4}>{service.serviceCatalog.serviceName}</Typography.Title>
            <Space wrap>
              <Tag>{t(`serviceOrigin${service.origin}`)}</Tag>
              <Tag color={service.urgency === "IMMEDIATE" ? "red" : service.urgency === "SOON" ? "orange" : "blue"}>
                {t(`serviceUrgency${service.urgency}`)}
              </Tag>
              <Tag color={service.approved ? "green" : service.status === "DECLINED" ? "default" : "gold"}>
                {service.approved ? t("serviceApproved") : t(`serviceStatus${service.status}`)}
              </Tag>
              <Tag color={service.qcStatus === "PASSED" ? "green" : service.qcStatus === "FAILED" ? "red" : "cyan"}>
                QC · {t(`serviceQc${service.qcStatus}`)}
              </Tag>
            </Space>
          </div>}
          extra={<Space>
            <Button
              danger
              type="text"
              icon={<DeleteOutlined />}
              disabled={!editable}
              loading={remove.isPending}
              onClick={() => remove.mutate(service.id)}
            />
          </Space>}
        >
          <div className="service-story-grid">
            <section>
              <span className="service-section-kicker"><FileSearchOutlined /> {t("whyThisService")}</span>
              <b>{data.repairOrder.customerConcern || t("customerRequest")}</b>
              {(data.repairOrder.inspection?.items ?? [])
                .filter(item => item.condition !== "PASS")
                .slice(0, 2)
                .map(item => <small key={item.id}>{item.name}: {item.note || t(item.condition.toLowerCase())}</small>)}
            </section>
            <section>
              <span className="service-section-kicker"><ToolOutlined /> {t("recommendation")}</span>
              <b>{service.recommendation || service.serviceCatalog.description || service.serviceCatalog.serviceName}</b>
              <small>{service.serviceCatalog.sourceRules[0]?.message || t("knowledgeRuleApplied")}</small>
            </section>
          </div>
          <div className="service-metrics">
            <SummaryTile label={t("oemLaborTime")} value={`${service.oemLaborTime} ${t("hours")}`} />
            <SummaryTile label={t("shopLaborTime")} value={`${service.shopLaborTime} ${t("hours")}`} />
            <SummaryTile label={t("finalLaborTime")} value={`${service.finalLaborTime} ${t("hours")}`} />
            <SummaryTile label={t("laborRate")} value={formatCurrency(service.laborRate)} />
            <SummaryTile label={t("laborAmount")} value={formatCurrency(service.laborAmount)} />
          </div>
          {service.overrideReason && <Alert
            type="warning"
            showIcon
            message={`${t("laborOverride")}: ${service.overrideReason}`}
            description={service.overrideByUser?.displayName}
          />}
          <div className="service-card-actions">
            {canRunLabor && !service.workSession && service.laborEntry?.status !== "COMPLETED" && data.repairOrder.status === "IN_PROGRESS"
              && <Button type="primary" icon={<PlayCircleOutlined />} loading={startService.isPending} onClick={() => startService.mutate(service)}>
                {t("startServiceWork")}
              </Button>}
            {canRunLabor && service.workSession?.status === "RUNNING" && data.repairOrder.status === "IN_PROGRESS"
              && <Button icon={<PauseCircleOutlined />} loading={pauseService.isPending} onClick={() => pauseService.mutate(service)}>
                {t("pause")}
              </Button>}
            {canRunLabor && service.workSession?.status === "PAUSED" && data.repairOrder.status === "IN_PROGRESS"
              && <Button type="primary" icon={<PlayCircleOutlined />} loading={resumeService.isPending} onClick={() => resumeService.mutate(service)}>
                {t("resume")}
              </Button>}
            {canRunLabor && service.workSession && service.workSession.status !== "COMPLETED" && data.repairOrder.status === "IN_PROGRESS"
              && <Button type="primary" icon={<CheckCircleOutlined />} loading={completeService.isPending} onClick={() => completeService.mutate(service)}>
                {t("completeServiceWork")}
              </Button>}
            {canPerformQc && data.repairOrder.status === "QUALITY_CHECK" && service.status === "COMPLETED" && service.qcStatus !== "PASSED" && <>
              <Button danger loading={serviceQc.isPending} onClick={() => serviceQc.mutate({ serviceId: service.id, passed: false })}>{t("qcFail")}</Button>
              <Button type="primary" icon={<CheckCircleOutlined />} loading={serviceQc.isPending} onClick={() => serviceQc.mutate({ serviceId: service.id, passed: true })}>{t("qcPass")}</Button>
            </>}
            <Button disabled={!editable} onClick={() => openOverride(service)}>{t("overrideLabor")}</Button>
            <Button disabled={!editable} onClick={() => setPartServiceId(service.id)}>{t("addPartToService")}</Button>
            <Button icon={<CameraOutlined />} disabled={["CLOSED", "CANCELLED"].includes(data.repairOrder.status)} onClick={() => setEvidenceServiceId(service.id)}>
              {t("addEvidence")}
            </Button>
          </div>
          {overrideServiceId === service.id && <form className="service-inline-form" onSubmit={event => {
            event.preventDefault();
            if (overrideReason.trim()) override.mutate();
          }}>
            <InputNumber min={0} step={0.1} precision={2} value={finalLaborTime} onChange={value => setFinalLaborTime(value ?? 0)} addonBefore={t("finalLaborTime")} />
            <InputNumber min={0} precision={2} value={overrideLaborRate} onChange={value => setOverrideLaborRate(value ?? 0)} formatter={formatGroupedInput} parser={parseGroupedInput} addonBefore={`${t("laborRate")} $`} />
            <Input value={overrideReason} onChange={event => setOverrideReason(event.target.value)} placeholder={t("overrideReasonRequired")} />
            <Button type="primary" htmlType="submit" disabled={!overrideReason.trim()} loading={override.isPending}>{t("save")}</Button>
          </form>}
          {partServiceId === service.id && <form className="service-inline-form service-part-form" onSubmit={event => {
            event.preventDefault();
            if (partNumber.trim() && partDescription.trim()) addPart.mutate();
          }}>
            <Input value={partNumber} onChange={event => setPartNumber(event.target.value.toUpperCase())} placeholder={t("partNumber")} />
            <Input value={partDescription} onChange={event => setPartDescription(event.target.value)} placeholder={t("materialName")} />
            <InputNumber min={0.01} precision={2} value={partQty} onChange={value => setPartQty(value ?? 1)} addonBefore={t("quantity")} />
            <InputNumber min={0} precision={2} value={partPrice} onChange={value => setPartPrice(value ?? 0)} formatter={formatGroupedInput} parser={parseGroupedInput} addonBefore={`${t("unitPrice")} $`} />
            <Input value={partSupplier} onChange={event => setPartSupplier(event.target.value)} placeholder={t("materialSupplier")} />
            <Button type="primary" htmlType="submit" disabled={!partNumber.trim() || !partDescription.trim()} loading={addPart.isPending}>{t("save")}</Button>
          </form>}
          {evidenceServiceId === service.id && <form className="service-evidence-form" onSubmit={event => {
            event.preventDefault();
            if (evidenceFile || evidenceSummary.trim() || evidenceValue.trim()) addEvidence.mutate();
          }}>
            <Select value={evidenceType} onChange={setEvidenceType} options={[
              "PHOTO", "VIDEO", "MEASUREMENT", "OBD", "DOCUMENT", "AUDIO", "SIGNATURE", "OEM_REFERENCE"
            ].map(value => ({ value, label: t(`evidence${value}`) }))} />
            <Input value={evidenceSummary} onChange={event => setEvidenceSummary(event.target.value)} placeholder={t("evidenceSummary")} />
            <Input value={evidenceValue} onChange={event => setEvidenceValue(event.target.value)} placeholder={t("measurementOrCode")} />
            <Input value={evidenceUnit} onChange={event => setEvidenceUnit(event.target.value)} placeholder={t("unit")} />
            <Upload
              showUploadList={false}
              accept="image/*,video/*,.pdf,.mp3,.m4a,.wav"
              beforeUpload={file => { setEvidenceFile(file); return false; }}
            >
              <Button icon={<UploadOutlined />}>{evidenceFile?.name || t("chooseEvidenceFile")}</Button>
            </Upload>
            <label className="evidence-visibility"><Switch checked={evidenceCustomerVisible} onChange={setEvidenceCustomerVisible} /> {t("customerVisible")}</label>
            <Button type="primary" htmlType="submit" disabled={!evidenceFile && !evidenceSummary.trim() && !evidenceValue.trim()} loading={addEvidence.isPending}>
              {t("saveEvidence")}
            </Button>
          </form>}
          <Typography.Title level={5}>{t("recommendedParts")}</Typography.Title>
          <Table
            size="small"
            rowKey="id"
            pagination={false}
            dataSource={service.parts}
            scroll={{ x: 760 }}
            locale={{ emptyText: t("noRecommendedParts") }}
            columns={[
              { title: t("source"), dataIndex: "partSource", width: 140, render: (value: string) => <Tag>{value.replaceAll("_", " ")}</Tag> },
              { title: t("partNumber"), dataIndex: "partNumber", width: 150 },
              { title: t("materialName"), dataIndex: "description", width: 220 },
              { title: t("quantity"), dataIndex: "qty", width: 90, align: "right" as const },
              { title: t("unitPrice"), dataIndex: "unitPrice", width: 110, align: "right" as const, render: formatCurrency },
              { title: t("amount"), dataIndex: "amount", width: 110, align: "right" as const, render: formatCurrency },
              { title: t("issueStatus"), key: "issue", width: 170, render: (_: unknown, item: RepairOrderServiceItem["parts"][number]) =>
                item.issued
                  ? <Tag color="green">{t("issued")}</Tag>
                  : canIssueParts && item.inventoryItem && data.repairOrder.status === "IN_PROGRESS"
                    ? <Button
                        size="small"
                        type="primary"
                        loading={issueServicePart.isPending}
                        onClick={() => issueServicePart.mutate({
                          serviceId: service.id,
                          partId: item.id,
                          quantity: Math.ceil(Number(item.qty))
                        })}
                      >{t("issue")} {item.qty}</Button>
                    : <Tag>{item.partSource === "CUSTOMER_SUPPLIED" ? t("customerSuppliedParts") : t("notIssued")}</Tag> }
            ]}
          />
          <div className="service-evidence-section">
            <div className="service-subsection-heading">
              <span><CameraOutlined /> {t("evidence")}</span>
              <Tag>{service.evidence.length}</Tag>
            </div>
            {service.evidence.length
              ? <div className="service-evidence-strip">{service.evidence.map(item => <article key={item.id}>
                  <Tag color="blue">{t(`evidence${item.type}`)}</Tag>
                  <b>{item.summary || item.fileName || `${item.value ?? ""} ${item.unit ?? ""}`.trim()}</b>
                  <small>{item.capturedBy?.displayName ?? "System"} · {formatDateTime(item.createdAt)}</small>
                  <small>{item.customerVisible ? t("customerVisible") : t("internalOnly")}</small>
                </article>)}</div>
              : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("noServiceEvidence")} />}
          </div>
          <Typography.Title level={5}>{t("inspectionRecommendations")}</Typography.Title>
          <List
            size="small"
            dataSource={service.serviceCatalog.sourceRules}
            locale={{ emptyText: t("noRecommendations") }}
            renderItem={rule => <List.Item>
              <Space align="start">
                <Tag color={rule.ruleType === "INSPECTION" ? "cyan" : rule.ruleType === "RECOMMEND_SERVICE" ? "purple" : "blue"}>
                  {t(`rule${rule.ruleType}`)}
                </Tag>
                <span>{rule.message}</span>
              </Space>
            </List.Item>}
          />
          <div className="service-production-grid">
            <section><UserOutlined /><span>{t("technician")}
              {canRunLabor
                ? <Select
                    size="small"
                    value={service.technician?.id ?? service.laborEntry?.technician?.id}
                    placeholder={t("selectTechnician")}
                    loading={assignTechnician.isPending}
                    onChange={technicianId => assignTechnician.mutate({ serviceId: service.id, technicianId })}
                    options={data.staff.filter(member => member.userRoles.some(item =>
                      ["Technician", "Manager", "Admin"].includes(item.role.name)
                    )).map(member => ({ value: member.id, label: member.displayName }))}
                  />
                : <b>{service.technician?.displayName ?? service.laborEntry?.technician?.displayName ?? t("unassigned")}</b>}
            </span></section>
            <section><ClockCircleOutlined /><span>{t("workStatus")}<b>{service.workSession
              ? <>{t(`workSession${service.workSession.status}`)} · <ServiceWorkTimer session={service.workSession} /></>
              : t(`serviceStatus${service.status}`)}</b></span></section>
            <section><ToolOutlined /><span>{t("partsReadiness")}<b>{service.parts.filter(part => part.issued).length}/{service.parts.length}</b></span></section>
            <section><SafetyCertificateOutlined /><span>{t("warranty")}<b>{service.laborWarrantyMonths ? `${service.laborWarrantyMonths} ${t("months")}` : t("notConfigured")}</b></span></section>
            <section className="service-price-total"><DollarOutlined /><span>{t("serviceTotal")}<b>{formatCurrency(serviceTotal(service))}</b></span></section>
          </div>
          <div className="service-timeline">
            <span className="service-section-kicker"><ClockCircleOutlined /> {t("serviceTimeline")}</span>
            {serviceTimeline(data, service.id).length
              ? serviceTimeline(data, service.id).slice(0, 4).map(item => <div key={item.id}><b>{item.title}</b><small>{formatDateTime(item.createdAt)}</small></div>)
              : <small>{t("noServiceTimeline")}</small>}
          </div>
        </Card>)}
    </div>
    <Typography.Paragraph className="manual-fallback-note" type="secondary">
      {t("manualLaborPartFallback")}
    </Typography.Paragraph>
  </Card>;
}

export function ShopWorkSection(props: Props) {
  const { id, data, text, refresh } = props;
  const [searchParams] = useSearchParams();
  const t = (key: string) => text[key] ?? key;
  const order = data.repairOrder;
  const editable = ["IN_PROGRESS", "SUPPLEMENT_REQUIRED"].includes(order.status);
  const [showLaborForm, setShowLaborForm] = useState(false);
  const [showMaterialForm, setShowMaterialForm] = useState(false);
  const [showCustomerPartForm, setShowCustomerPartForm] = useState(false);
  const [laborDescription, setLaborDescription] = useState("");
  const [laborHours, setLaborHours] = useState(1);
  const [laborRate, setLaborRate] = useState(0);
  const [laborTechnicianId, setLaborTechnicianId] = useState(order.technicianId ?? undefined);
  const [materialSource, setMaterialSource] = useState<"STOCK" | "CUSTOM">("STOCK");
  const [materialPartId, setMaterialPartId] = useState<string>();
  const [customMaterialSku, setCustomMaterialSku] = useState("");
  const [customMaterialName, setCustomMaterialName] = useState("");
  const [materialSupplier, setMaterialSupplier] = useState("");
  const [customMaterialUnitPrice, setCustomMaterialUnitPrice] = useState(0);
  const [customMaterialNotes, setCustomMaterialNotes] = useState("");
  const [materialQuantity, setMaterialQuantity] = useState(1);
  const [materialTechnicianId, setMaterialTechnicianId] = useState(order.technicianId ?? undefined);
  const [customerPartName, setCustomerPartName] = useState("");
  const [customerPartQuantity, setCustomerPartQuantity] = useState(1);
  const [customerPartNotes, setCustomerPartNotes] = useState("");
  const [customerPartTechnicianId, setCustomerPartTechnicianId] = useState(order.technicianId ?? undefined);
  const [workshopNotes, setWorkshopNotes] = useState(order.workshopNotes ?? "");
  const technicians = data.staff.map(item => ({ value: item.id, label: item.displayName }));
  const addLabor = useWorkflowMutation<void>(() => repairOrdersApi.addWorkshopLabor(id, {
    description: laborDescription,
    hours: laborHours,
    rate: laborRate,
    technicianId: laborTechnicianId
  }), () => {
    setLaborDescription("");
    setShowLaborForm(false);
    refresh();
  });
  const addMaterial = useWorkflowMutation<void>(() => repairOrdersApi.addWorkshopMaterial(id, {
    partId: materialPartId!,
    quantity: materialQuantity,
    supplier: materialSupplier || undefined,
    technicianId: materialTechnicianId
  }), () => {
    setMaterialPartId(undefined);
    setMaterialSupplier("");
    setShowMaterialForm(false);
    refresh();
  });
  const addCustomMaterial = useWorkflowMutation<void>(() => repairOrdersApi.addCustomWorkshopMaterial(id, {
    sku: customMaterialSku || undefined,
    name: customMaterialName,
    supplier: materialSupplier || undefined,
    quantity: materialQuantity,
    unitPrice: customMaterialUnitPrice,
    notes: customMaterialNotes || undefined,
    technicianId: materialTechnicianId
  }), () => {
    setCustomMaterialSku("");
    setCustomMaterialName("");
    setMaterialSupplier("");
    setCustomMaterialUnitPrice(0);
    setCustomMaterialNotes("");
    setShowMaterialForm(false);
    refresh();
  });
  const addCustomerPart = useWorkflowMutation<void>(() => repairOrdersApi.addCustomerSuppliedPart(id, {
    name: customerPartName,
    quantity: customerPartQuantity,
    notes: customerPartNotes || undefined,
    technicianId: customerPartTechnicianId
  }), () => {
    setCustomerPartName("");
    setCustomerPartNotes("");
    setShowCustomerPartForm(false);
    refresh();
  });
  const saveNotes = useWorkflowMutation<void>(() => repairOrdersApi.updateWorkflowContext(id, {
    workshopNotes
  }), refresh);
  const finish = useWorkflowMutation<void>(() => repairOrdersApi.qualityCheck(id), refresh);
  const passInspection = useWorkflowMutation<void>(() => repairOrdersApi.readyDelivery(id), refresh);
  const startLabor = useWorkflowMutation((laborId: string) => repairOrdersApi.startLabor(id, laborId), refresh);
  const completeLabor = useWorkflowMutation((laborId: string) => repairOrdersApi.completeLabor(id, laborId), refresh);
  const issuePart = useWorkflowMutation(
    (input: { id: string; quantity: number }) => repairOrdersApi.issuePart(id, input.id, input.quantity),
    refresh
  );
  const laborTotal = order.laborEntries.reduce((sum, item) => sum + Number(item.total), 0);
  const catalogPartsTotal = order.parts.reduce((sum, item) => sum + Number(item.unitPrice) * item.requiredQuantity, 0);
  const customPartsTotal = order.customRepairMaterials.reduce((sum, item) => sum + Number(item.total), 0);
  const partsTotal = catalogPartsTotal + customPartsTotal;
  const otherTotal = Number(order.invoice?.shopSupplyAmount ?? data.estimate?.shopSupplyAmount ?? 0);
  const taxTotal = Number(order.invoice?.taxAmount ?? data.estimate?.taxAmount ?? partsTotal * 0.0975);
  const discountTotal = Number(order.invoice?.discountAmount ?? data.estimate?.discountAmount ?? 0);
  const grandTotal = Number(
    order.invoice?.total
    ?? data.estimate?.total
    ?? Math.max(laborTotal + partsTotal + otherTotal + taxTotal - discountTotal, 0)
  );
  const materialRows = [
    ...order.parts.map(item => ({
      key: `stock-${item.id}`,
      sku: item.part.sku,
      name: item.part.name,
      quantity: item.requiredQuantity,
      unitPrice: Number(item.unitPrice),
      total: Number(item.unitPrice) * item.requiredQuantity,
      technician: item.technician?.displayName ?? order.technician?.displayName ?? t("unassigned"),
      supplier: item.supplier ?? "",
      notes: ""
      ,
      repairOrderPartId: item.id,
      remaining: item.requiredQuantity - item.issuedQuantity
    })),
    ...order.customRepairMaterials.map(item => ({
      key: `custom-${item.id}`,
      sku: item.sku || "—",
      name: item.name,
      quantity: item.quantity,
      unitPrice: Number(item.unitPrice),
      total: Number(item.total),
      technician: item.technician?.displayName ?? t("unassigned"),
      supplier: item.supplier ?? "",
      notes: item.notes ?? ""
      ,
      repairOrderPartId: null,
      remaining: 0
    }))
  ];
  const allLaborCompleted = order.laborEntries.length > 0 && order.laborEntries.every(item => item.status === "COMPLETED");
  const allPartsIssued = order.parts.every(item => item.issuedQuantity >= item.requiredQuantity);
  const hasRepairWork = order.laborEntries.length > 0 || materialRows.length > 0;
  const canFinish = order.status === "IN_PROGRESS" && hasRepairWork && allLaborCompleted && allPartsIssued;
  useEffect(() => {
    const target = searchParams.get("print");
    if (target === "workshop") printDocument("workshop", `车间维修施工单-${order.roNumber}`);
    if (target === "settlement") printDocument("settlement", `结算单-${order.roNumber}`);
  }, [order.roNumber, searchParams]);
  return <div className="workflow-section-stack">
    {(addLabor.isError || addMaterial.isError || addCustomMaterial.isError || addCustomerPart.isError || saveNotes.isError || finish.isError || passInspection.isError || startLabor.isError || completeLabor.isError || issuePart.isError) && <MutationError error={addLabor.error || addMaterial.error || addCustomMaterial.error || addCustomerPart.error || saveNotes.error || finish.error || passInspection.error || startLabor.error || completeLabor.error || issuePart.error} />}
    <div className="workflow-summary-strip">
      <SummaryTile label={t("currentStatus")} value={order.status.replaceAll("_", " ")} />
      <SummaryTile label={t("vehicleRepairProjects")} value={String(order.laborEntries.length)} />
      <SummaryTile label={t("laborCost")} value={formatCurrency(laborTotal)} />
      <SummaryTile label={t("materialCost")} value={formatCurrency(partsTotal)} />
      <SummaryTile label={t("technician")} value={order.technician?.displayName ?? t("unassigned")} />
    </div>
    <Space wrap className="workshop-primary-actions">
      <Button
        type="primary"
        icon={<CheckCircleOutlined />}
        loading={finish.isPending}
        disabled={!canFinish}
        onClick={() => finish.mutate()}
      >{t("completeAndSettle")}</Button>
      {order.status === "QUALITY_CHECK" && <Button
        type="primary"
        icon={<CheckCircleOutlined />}
        loading={passInspection.isPending}
        onClick={() => passInspection.mutate()}
      >{t("passInspectionIssueInvoice")}</Button>}
    </Space>
    <Card title={t("labor")} extra={<Button aria-label={t("addLabor")} title={t("addLabor")} icon={<PlusOutlined />} disabled={!editable} onClick={() => setShowLaborForm(value => !value)} />}>
      {showLaborForm && <form onSubmit={event => { event.preventDefault(); if (laborDescription.trim() && laborHours > 0) addLabor.mutate(); }}>
        <Space wrap className="workflow-inline-form workshop-add-form">
          <Input value={laborDescription} onChange={event => setLaborDescription(event.target.value)} placeholder={t("repairItem")} />
          <InputNumber min={0.1} step={0.1} precision={2} value={laborHours} onChange={value => setLaborHours(value ?? 1)} addonBefore={t("hours")} />
          <InputNumber min={0} precision={2} value={laborRate} formatter={formatGroupedInput} parser={parseGroupedInput} onChange={value => setLaborRate(value ?? 0)} addonBefore={`${t("unitPrice")} $`} />
          <InputNumber readOnly controls={false} precision={2} value={laborHours * laborRate} formatter={formatGroupedInput} addonBefore={`${t("amount")} $`} />
          <Select allowClear value={laborTechnicianId} onChange={setLaborTechnicianId} placeholder={t("selectTechnician")} options={technicians} />
          <Button type="primary" htmlType="submit" loading={addLabor.isPending} disabled={!laborDescription.trim()}>{t("save")}</Button>
        </Space>
      </form>}
      <Table rowKey="id" pagination={false} dataSource={order.laborEntries} locale={{ emptyText: text.awaitingApprovedEstimate }} columns={[
        { title: t("sequence"), key: "sequence", width: 68, render: (_: unknown, __: unknown, index: number) => index + 1 },
        { title: text.repairItem, dataIndex: "description" },
        { title: text.hours, dataIndex: "hours", align: "right" as const },
        { title: text.unitPrice, dataIndex: "rate", align: "right" as const, render: formatCurrency },
        { title: text.amount, dataIndex: "total", align: "right" as const, render: formatCurrency },
        { title: text.technician, key: "technician", render: (_: unknown, item: WorkflowData["repairOrder"]["laborEntries"][number]) => item.technician?.displayName ?? text.unassigned },
        { title: t("status"), dataIndex: "status", render: (status: string) => <LaborStatusTag status={status} /> },
        { title: "", key: "actions", render: (_: unknown, item: WorkflowData["repairOrder"]["laborEntries"][number]) => <Space>
          {item.status === "PENDING" && <Button disabled={order.status !== "IN_PROGRESS"} loading={startLabor.isPending} onClick={() => startLabor.mutate(item.id)}>{t("start")}</Button>}
          {item.status !== "COMPLETED" && <Button type="primary" disabled={order.status !== "IN_PROGRESS"} loading={completeLabor.isPending} onClick={() => completeLabor.mutate(item.id)}>{t("complete")}</Button>}
        </Space> }
      ]} />
    </Card>
    <Card title={t("materials")} extra={<Button aria-label={t("addMaterial")} title={t("addMaterial")} icon={<PlusOutlined />} disabled={!editable} onClick={() => setShowMaterialForm(value => !value)} />}>
      {showMaterialForm && <form onSubmit={event => {
        event.preventDefault();
        if (materialSource === "STOCK" && materialPartId && materialQuantity > 0) addMaterial.mutate();
        if (materialSource === "CUSTOM" && customMaterialName.trim() && materialQuantity > 0) addCustomMaterial.mutate();
      }}>
        <Space wrap className="workflow-inline-form workshop-add-form">
          <Select value={materialSource} onChange={setMaterialSource} options={[
            { value: "STOCK", label: t("inventoryMaterial") },
            { value: "CUSTOM", label: t("freeMaterial") }
          ]} />
          {materialSource === "STOCK" ? <Select
            showSearch
            optionFilterProp="label"
            value={materialPartId}
            onChange={setMaterialPartId}
            placeholder={t("selectPart")}
            options={data.availableParts.map(part => ({ value: part.id, label: `${part.sku} · ${part.name} · ${t("onHand")} ${part.onHand}` }))}
          /> : <>
            <Input value={customMaterialSku} onChange={event => setCustomMaterialSku(event.target.value.toUpperCase())} placeholder={t("sku")} />
            <Input value={customMaterialName} onChange={event => setCustomMaterialName(event.target.value)} placeholder={t("materialName")} />
            <InputNumber min={0} precision={2} value={customMaterialUnitPrice} formatter={formatGroupedInput} parser={parseGroupedInput} onChange={value => setCustomMaterialUnitPrice(value ?? 0)} addonBefore={`${t("unitPrice")} $`} />
            <Input value={customMaterialNotes} onChange={event => setCustomMaterialNotes(event.target.value)} placeholder={t("notes")} />
          </>}
          <InputNumber min={1} precision={0} value={materialQuantity} onChange={value => setMaterialQuantity(value ?? 1)} addonBefore={t("quantity")} />
          <Select allowClear value={materialTechnicianId} onChange={setMaterialTechnicianId} placeholder={t("selectTechnician")} options={technicians} />
          <Input value={materialSupplier} onChange={event => setMaterialSupplier(event.target.value)} placeholder={t("materialSupplier")} />
          <Button
            type="primary"
            htmlType="submit"
            loading={addMaterial.isPending || addCustomMaterial.isPending}
            disabled={materialSource === "STOCK" ? !materialPartId : !customMaterialName.trim()}
          >{t("save")}</Button>
        </Space>
      </form>}
      <Table rowKey="key" pagination={false} dataSource={materialRows} locale={{ emptyText: text.awaitingApprovedEstimate }} columns={[
        { title: t("sequence"), key: "sequence", width: 68, render: (_: unknown, __: unknown, index: number) => index + 1 },
        { title: "SKU", dataIndex: "sku" },
        { title: text.materialName, dataIndex: "name" },
        { title: text.quantity, dataIndex: "quantity", align: "right" as const },
        { title: text.unitPrice, dataIndex: "unitPrice", align: "right" as const, render: formatCurrency },
        { title: text.amount, dataIndex: "total", align: "right" as const, render: formatCurrency },
        { title: text.technician, dataIndex: "technician" },
        { title: t("notes"), dataIndex: "notes", render: (value: string) => value || "—" },
        { title: t("materialSupplier"), dataIndex: "supplier", render: (value: string) => value || "—" }
        ,
        { title: "", key: "actions", render: (_: unknown, item: typeof materialRows[number]) => item.repairOrderPartId && item.remaining > 0
          ? <Button type="primary" disabled={order.status !== "IN_PROGRESS"} loading={issuePart.isPending} onClick={() => issuePart.mutate({ id: item.repairOrderPartId!, quantity: item.remaining })}>{t("issue")} {item.remaining}</Button>
          : null }
      ]} />
    </Card>
    <Card title={t("customerSuppliedParts")} extra={<Button aria-label={t("addCustomerSuppliedPart")} title={t("addCustomerSuppliedPart")} icon={<PlusOutlined />} disabled={!editable} onClick={() => setShowCustomerPartForm(value => !value)} />}>
      {showCustomerPartForm && <form onSubmit={event => { event.preventDefault(); if (customerPartName.trim() && customerPartQuantity > 0) addCustomerPart.mutate(); }}>
        <Space wrap className="workflow-inline-form workshop-add-form">
          <Input value={customerPartName} onChange={event => setCustomerPartName(event.target.value)} placeholder={t("materialName")} />
          <InputNumber min={1} precision={0} value={customerPartQuantity} onChange={value => setCustomerPartQuantity(value ?? 1)} addonBefore={t("quantity")} />
          <Select allowClear value={customerPartTechnicianId} onChange={setCustomerPartTechnicianId} placeholder={t("selectTechnician")} options={technicians} />
          <Input value={customerPartNotes} onChange={event => setCustomerPartNotes(event.target.value)} placeholder={t("notes")} />
          <Button type="primary" htmlType="submit" loading={addCustomerPart.isPending} disabled={!customerPartName.trim()}>{t("save")}</Button>
        </Space>
      </form>}
      <Table rowKey="id" pagination={false} dataSource={order.customerSuppliedParts} locale={{ emptyText: t("noCustomerSuppliedParts") }} columns={[
        { title: t("sequence"), key: "sequence", width: 68, render: (_: unknown, __: unknown, index: number) => index + 1 },
        { title: t("materialName"), dataIndex: "name" },
        { title: t("quantity"), dataIndex: "quantity", align: "right" as const },
        { title: t("technician"), key: "technician", render: (_: unknown, item: WorkflowData["repairOrder"]["customerSuppliedParts"][number]) => item.technician?.displayName ?? t("unassigned") },
        { title: t("notes"), dataIndex: "notes", render: (value: string | null) => value || "—" }
      ]} />
    </Card>
    <Card title={t("workshopNotesAndDisclaimer")}>
      <Typography.Title level={5}>{t("workshopNotes")}</Typography.Title>
      <Input.TextArea rows={3} value={workshopNotes} onChange={event => setWorkshopNotes(event.target.value)} placeholder={t("workshopNotesPlaceholder")} />
      <Button className="workshop-notes-save" icon={<CheckCircleOutlined />} loading={saveNotes.isPending} onClick={() => saveNotes.mutate()}>{t("save")}</Button>
      <Typography.Title level={5}>{t("disclaimer")}</Typography.Title>
      <Alert type="warning" showIcon message={t("customerSuppliedPartDisclaimer")} />
    </Card>
    <PhotosSection {...props} />
    <WorkshopPrintSheet data={data} text={text} laborTotal={laborTotal} materialTotal={partsTotal} materials={materialRows} />
    <SettlementPrintSheet data={data} laborTotal={laborTotal} materialTotal={partsTotal} otherTotal={otherTotal} discountTotal={discountTotal} taxTotal={taxTotal} grandTotal={grandTotal} materials={materialRows} />
  </div>;
}

function WorkshopPrintSheet({ data, text, laborTotal, materialTotal, materials }: {
  data: WorkflowData;
  text: TextMap;
  laborTotal: number;
  materialTotal: number;
  materials: Array<{ key: string; sku: string; name: string; quantity: number; unitPrice: number; total: number; technician: string; notes: string }>;
}) {
  const order = data.repairOrder;
  const taxTotal = materialTotal * 0.0975;
  const discountTotal = Number(data.estimate?.discountAmount ?? 0);
  const grandTotal = Math.max(laborTotal + materialTotal + taxTotal - discountTotal, 0);
  return createPortal(<section className="print-document workshop-print-sheet" aria-hidden="true">
    <BilingualDocumentHeader titleEn="REPAIR ORDER" titleZh="车间维修施工单" documentNumber={order.roNumber} order={order} />
    <DocumentConcern order={order} />
    <DocumentSectionTitle en="LABOR" zh="人工" />
    <LaborDocumentTable lines={order.laborEntries.map(item => ({
      id: item.id,
      description: item.description,
      hours: item.hours,
      rate: item.rate,
      total: item.total,
      technician: item.technician?.displayName ?? text.unassigned
    }))} showTechnician showTechnicianSignature />
    <DocumentSectionTitle en="PARTS / MATERIALS" zh="零件 / 材料" />
    <MaterialDocumentTable lines={materials.map(item => ({
      id: item.key,
      sku: item.sku,
      name: item.name,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      total: item.total,
      technician: item.technician
    }))} showTechnician showTechnicianSignature />
    {!!order.customerSuppliedParts.length && <>
      <DocumentSectionTitle en="CUSTOMER-SUPPLIED PARTS" zh="客户自带配件" />
      <table className="repair-document-table"><thead><tr><th>序号<br />NO.</th><th>材料名称<br />DESCRIPTION</th><th>数量<br />QTY</th><th>技师<br />TECHNICIAN</th><th>备注<br />NOTES</th><th className="technician-signature-column">技师签名<br />TECH SIGNATURE</th></tr></thead>
        <tbody>{order.customerSuppliedParts.map((item, index) => <tr key={item.id}><td>{index + 1}</td><td>{item.name}</td><td>{item.quantity}</td><td>{item.technician?.displayName ?? text.unassigned}</td><td>{item.notes || "—"}</td><td className="technician-signature-cell">________________</td></tr>)}</tbody>
      </table>
    </>}
    <div className="repair-document-lower">
      <DocumentNotes notes={order.workshopNotes} />
      <DocumentTotals labor={laborTotal} materials={materialTotal} discount={discountTotal} tax={taxTotal} total={grandTotal} />
    </div>
    <BilingualDocumentFooter signatureRole="SHOP_SUPERVISOR_OR_INSPECTOR" />
  </section>, document.body);
}

function SettlementPrintSheet({ data, laborTotal, materialTotal, otherTotal, discountTotal, taxTotal, grandTotal, materials }: {
  data: WorkflowData;
  laborTotal: number;
  materialTotal: number;
  otherTotal: number;
  discountTotal: number;
  taxTotal: number;
  grandTotal: number;
  materials: Array<{ key: string; sku: string; name: string; quantity: number; unitPrice: number; total: number; technician: string; notes: string }>;
}) {
  const order = data.repairOrder;
  const paid = Number(order.invoice?.amountPaid ?? 0);
  const balance = Number(order.invoice?.balance ?? grandTotal - paid);
  return createPortal(<section className="print-document settlement-print-sheet" aria-hidden="true">
    <BilingualDocumentHeader titleEn="INVOICE" titleZh="结算单" documentNumber={order.invoice?.invoiceNumber ?? order.roNumber} order={order} />
    <DocumentSectionTitle en="LABOR" zh="人工" />
    <LaborDocumentTable lines={order.laborEntries.map(item => ({
      id: item.id,
      description: item.description,
      hours: item.hours,
      rate: item.rate,
      total: item.total,
      discount: Math.max(Number(item.hours) * Number(item.rate) - Number(item.total), 0)
    }))} showDiscount />
    <DocumentSectionTitle en="PARTS / MATERIALS" zh="零件 / 材料" />
    <MaterialDocumentTable lines={materials.map(item => ({ id: item.key, sku: item.sku, name: item.name, quantity: item.quantity, unitPrice: item.unitPrice, total: item.total }))} />
    <div className="repair-document-lower">
      <DocumentNotes notes={order.workshopNotes || order.internalNotes} />
      <DocumentTotals labor={laborTotal} materials={materialTotal} other={otherTotal} discount={discountTotal} tax={taxTotal} total={grandTotal} paid={paid} balance={balance} showBalance />
    </div>
    <BilingualDocumentFooter />
  </section>, document.body);
}

function BilingualDocumentHeader({ titleEn, titleZh, documentNumber, order }: {
  titleEn: string;
  titleZh: string;
  documentNumber: string;
  order: WorkflowData["repairOrder"];
}) {
  return <>
    <header className="repair-document-header">
      <div className="repair-document-shop"><b>AUTOFLOW PRO</b><strong>专业汽车维修 / PROFESSIONAL AUTO REPAIR</strong><span>{order.location.name}</span></div>
      <div className="repair-document-title"><h1>{titleEn}</h1><h2>{titleZh}</h2><span>{order.roNumber}</span></div>
    </header>
    <div className="repair-document-strip"><b>单据号 / DOCUMENT #</b><span>{documentNumber}</span><b>日期 / DATE</b><span>{formatDate(order.openedAt)}</span></div>
    <div className="repair-document-info-grid">
      <section>
        <h3>客户信息 / CUSTOMER INFORMATION</h3>
        <dl><dt>姓名 / NAME</dt><dd>{customerName(order.customer)}</dd><dt>电话 / TELEPHONE</dt><dd>{formatPhone(order.customer.phone)}</dd><dt>备用电话 / ALT. PHONE</dt><dd>{formatPhone(order.customer.alternatePhone)}</dd><dt>邮箱 / EMAIL</dt><dd>{order.customer.email || "—"}</dd></dl>
      </section>
      <section>
        <h3>车辆信息 / VEHICLE INFORMATION</h3>
        <dl><dt>品牌 / MAKE</dt><dd>{order.vehicle.make}</dd><dt>车型 / MODEL</dt><dd>{order.vehicle.model}</dd><dt>年份 / YEAR</dt><dd>{order.vehicle.year}</dd><dt>颜色 / COLOR</dt><dd>{order.vehicle.color || "—"}</dd><dt>VIN</dt><dd>{order.vehicle.vin}</dd><dt>车牌 / LICENSE PLATE</dt><dd>{order.vehicle.licensePlate || "—"}</dd><dt>里程 / ODOMETER</dt><dd>{formatMileage(order.mileageIn)}</dd><dt>发动机 / ENGINE</dt><dd>{order.vehicle.engine || "—"}</dd></dl>
      </section>
    </div>
  </>;
}

function DocumentConcern({ order }: { order: WorkflowData["repairOrder"] }) {
  return <div className="repair-document-concern"><b>客户诉求 / CUSTOMER CONCERN</b><span>{order.customerConcern || "—"}</span></div>;
}

function DocumentSectionTitle({ en, zh }: { en: string; zh: string }) {
  return <h3 className="repair-document-section-title">{zh} / {en}</h3>;
}

function LaborDocumentTable({ lines, showTechnician = false, showTechnicianSignature = false, showDiscount = false }: {
  lines: Array<{ id: string; description: string; hours: unknown; rate: unknown; total: unknown; technician?: string; discount?: unknown }>;
  showTechnician?: boolean;
  showTechnicianSignature?: boolean;
  showDiscount?: boolean;
}) {
  const columnCount = 5 + Number(showDiscount) + Number(showTechnician) + Number(showTechnicianSignature);
  return <table className="repair-document-table"><thead><tr><th>序号<br />NO.</th><th>维修项目<br />DESCRIPTION</th><th>工时<br />HOURS</th><th>工时单价<br />LABOR RATE</th>{showDiscount && <th>折扣<br />DISCOUNT</th>}<th>金额<br />TOTAL</th>{showTechnician && <th>技师<br />TECHNICIAN</th>}{showTechnicianSignature && <th className="technician-signature-column">技师签名<br />TECH SIGNATURE</th>}</tr></thead>
    <tbody>{lines.length ? lines.map((line, index) => <tr key={line.id}><td>{index + 1}</td><td>{line.description}</td><td>{String(line.hours)}</td><td>{formatCurrency(line.rate)}</td>{showDiscount && <td>{formatCurrency(line.discount ?? 0)}</td>}<td>{formatCurrency(line.total)}</td>{showTechnician && <td>{line.technician || "—"}</td>}{showTechnicianSignature && <td className="technician-signature-cell">________________</td>}</tr>) : <tr><td colSpan={columnCount}>—</td></tr>}</tbody>
  </table>;
}

function MaterialDocumentTable({ lines, showTechnician = false, showTechnicianSignature = false }: {
  lines: Array<{ id: string; sku: string; name: string; quantity: unknown; unitPrice: unknown; total: unknown; technician?: string }>;
  showTechnician?: boolean;
  showTechnicianSignature?: boolean;
}) {
  const columnCount = 6 + Number(showTechnician) + Number(showTechnicianSignature);
  return <table className="repair-document-table"><thead><tr><th>序号<br />NO.</th><th>SKU</th><th>材料说明<br />DESCRIPTION</th><th>数量<br />QTY</th><th>单价<br />PRICE</th><th>金额<br />TOTAL</th>{showTechnician && <th>技师<br />TECHNICIAN</th>}{showTechnicianSignature && <th className="technician-signature-column">技师签名<br />TECH SIGNATURE</th>}</tr></thead>
    <tbody>{lines.length ? lines.map((line, index) => <tr key={line.id}><td>{index + 1}</td><td>{line.sku}</td><td>{line.name}</td><td>{String(line.quantity)}</td><td>{formatCurrency(line.unitPrice)}</td><td>{formatCurrency(line.total)}</td>{showTechnician && <td>{line.technician || "—"}</td>}{showTechnicianSignature && <td className="technician-signature-cell">________________</td>}</tr>) : <tr><td colSpan={columnCount}>—</td></tr>}</tbody>
  </table>;
}

function DocumentNotes({ notes }: { notes: string | null | undefined }) {
  return <section className="repair-document-notes"><h3>备注 / NOTES</h3><p>{notes || "—"}</p><div><b>报价人 / QUOTED BY</b><span>AutoFlow Pro</span></div><div><b>经办人 / DONE BY</b><span>____________________</span></div><div><b>维修日期 / REPAIR ORDER DATE</b><span>____________________</span></div></section>;
}

function DocumentTotals({ labor, materials, other = 0, discount = 0, tax, total, paid = 0, balance = total - paid, showBalance = false }: {
  labor: number;
  materials: number;
  other?: number;
  discount?: number;
  tax: number;
  total: number;
  paid?: number;
  balance?: number;
  showBalance?: boolean;
}) {
  const subtotal = labor + materials + other;
  return <table className="repair-document-totals"><tbody>
    <tr><th>人工合计 / LABOR TOTAL</th><td>{formatCurrency(labor)}</td></tr>
    <tr><th>材料合计 / PARTS TOTAL</th><td>{formatCurrency(materials)}</td></tr>
    {!!other && <tr><th>其它费用 / OTHER</th><td>{formatCurrency(other)}</td></tr>}
    <tr><th>折扣 / DISCOUNT</th><td>{formatCurrency(discount)}</td></tr>
    <tr><th>小计 / SUBTOTAL</th><td>{formatCurrency(subtotal)}</td></tr>
    <tr><th>税费 / TAX 9.750%</th><td>{formatCurrency(tax)}</td></tr>
    <tr className="repair-document-total-row"><th>总计 / TOTAL</th><td>{formatCurrency(total)}</td></tr>
    {showBalance && <><tr><th>已付款 / PAID</th><td>{formatCurrency(paid)}</td></tr><tr className="repair-document-balance-row"><th>未结余额 / BALANCE DUE</th><td>{formatCurrency(balance)}</td></tr></>}
  </tbody></table>;
}

function BilingualDocumentFooter({ signatureRole = "CUSTOMER" }: {
  signatureRole?: "CUSTOMER" | "SHOP_SUPERVISOR_OR_INSPECTOR";
}) {
  const signatureLabel = signatureRole === "SHOP_SUPERVISOR_OR_INSPECTOR"
    ? "车间主管或检验员签名 / SHOP SUPERVISOR OR INSPECTOR"
    : "客户授权签字 / AUTHORIZED BY";
  return <footer className="repair-document-footer">
    <p>客户自带配件可能不适用本店配件质保。车辆、车内物品、付款义务及质保责任，以适用法律和本店书面政策为准。</p>
    <p>Customer-supplied parts may not be covered by the shop parts warranty. Vehicle custody, personal property, payment obligations, and warranty responsibilities remain subject to applicable law and the shop&apos;s written policy.</p>
    <div><span>{signatureLabel}: ____________________________________</span><span>日期 / DATE: ____________________</span></div>
  </footer>;
}

export function LaborSection({ id, data, text, refresh }: Props) {
  const startWork = useWorkflowMutation<void>(() => repairOrdersApi.startWork(id), refresh);
  const start = useWorkflowMutation((laborId: string) => repairOrdersApi.startLabor(id, laborId), refresh);
  const complete = useWorkflowMutation((laborId: string) => repairOrdersApi.completeLabor(id, laborId), refresh);
  return <div className="workflow-section-stack">
    {(startWork.isError || start.isError || complete.isError) && <MutationError error={startWork.error || start.error || complete.error} />}
    {data.repairOrder.status === "APPROVED" && <Card><Button type="primary" size="large" icon={<PlayCircleOutlined />} loading={startWork.isPending} onClick={() => startWork.mutate()}>{text.startRepairWork}</Button></Card>}
    <Card title={text.authorizedLabor}>
      <List dataSource={data.repairOrder.laborEntries} locale={{ emptyText: text.awaitingApprovedEstimate }} renderItem={labor => <List.Item actions={[
        labor.status === "PENDING" ? <Button key="start" icon={<PlayCircleOutlined />} disabled={data.repairOrder.status !== "IN_PROGRESS"} loading={start.isPending} onClick={() => start.mutate(labor.id)}>{text.start}</Button> : null,
        labor.status !== "COMPLETED" ? <Button key="complete" type="primary" icon={<CheckCircleOutlined />} disabled={data.repairOrder.status !== "IN_PROGRESS"} loading={complete.isPending} onClick={() => complete.mutate(labor.id)}>{text.complete}</Button> : null
      ].filter(Boolean) as ReactNode[]}>
        <List.Item.Meta title={<Space>{labor.description}<LaborStatusTag status={labor.status} /></Space>} description={`${labor.hours} ${text.hours} · ${formatCurrency(labor.total)} · ${labor.technician?.displayName ?? text.unassigned}`} />
      </List.Item>} />
    </Card>
  </div>;
}

export function PartsSection({ id, data, text, refresh }: Props) {
  const [searchParams] = useSearchParams();
  const scannedCode = searchParams.get("scan")?.trim();
  const scannedPart = scannedCode
    ? data.repairOrder.parts.find(item => item.part.sku.toUpperCase() === scannedCode.toUpperCase())
    : undefined;
  const issue = useWorkflowMutation((input: { partId: string; quantity: number }) => repairOrdersApi.issuePart(id, input.partId, input.quantity), refresh);
  return <div className="workflow-section-stack">
    {issue.isError && <MutationError error={issue.error} />}
    {scannedCode && <Alert
      type={scannedPart ? "success" : "warning"}
      showIcon
      message={scannedPart ? `${text.partMatched}: ${scannedPart.part.sku} · ${scannedPart.part.name}` : `${text.partNotOnRepairOrder}: ${scannedCode}`}
    />}
    <Card title={text.requiredParts}>
      <List dataSource={data.repairOrder.parts} locale={{ emptyText: text.awaitingApprovedEstimate }} renderItem={item => {
        const remaining = item.requiredQuantity - item.issuedQuantity;
        const stock = data.availableParts.find(part => part.id === item.part.id)?.onHand ?? 0;
        return <List.Item className={scannedPart?.id === item.id ? "scanned-part-row" : ""} actions={[<Button key="issue" type="primary" icon={<ToolOutlined />} disabled={remaining <= 0 || data.repairOrder.status !== "IN_PROGRESS"} loading={issue.isPending} onClick={() => issue.mutate({ partId: item.id, quantity: remaining })}>{remaining > 0 ? `${text.issue} ${remaining}` : text.issued}</Button>]}>
          <List.Item.Meta title={`${item.part.sku} · ${item.part.name}`} description={`${text.required}: ${item.requiredQuantity} · ${text.issued}: ${item.issuedQuantity} · ${text.onHand}: ${stock}`} />
        </List.Item>;
      }} />
    </Card>
  </div>;
}

export function PhotosSection({ id, data, text, refresh }: Props) {
  const upload = useWorkflowMutation((file: File) => repairOrdersApi.uploadRepairPhoto(id, file), refresh);
  return <Card title={text.repairPhotos} extra={<Upload showUploadList={false} beforeUpload={file => { upload.mutate(file); return false; }}><Button icon={<UploadOutlined />} loading={upload.isPending}>{text.upload}</Button></Upload>}>
    {upload.isError && <MutationError error={upload.error} />}
    <List grid={{ gutter: 16, xs: 1, sm: 2, lg: 3 }} dataSource={data.repairOrder.photos} locale={{ emptyText: text.noData }} renderItem={photo => <List.Item><Card size="small" title={photo.fileName}><Typography.Text type="secondary">{photo.caption || formatDateTime(photo.createdAt)}</Typography.Text></Card></List.Item>} />
  </Card>;
}

export function SettlementSummarySection({ id, data, text, refresh }: Props) {
  const estimate = data.estimate;
  const invoice = data.repairOrder.invoice;
  const draftEditable = data.repairOrder.status === "ESTIMATE" && estimate?.status === "DRAFT";
  const invoiceEditable = data.repairOrder.status === "READY_DELIVERY"
    && invoice?.status === "OPEN"
    && invoice.payments.length === 0
    && Number(invoice.amountPaid) === 0;
  const editable = draftEditable || invoiceEditable;
  const laborTotal = invoice
    ? invoice.lines.filter(line => line.type === "LABOR").reduce((sum, line) => sum + Number(line.lineTotal), 0)
    : estimate?.lines.filter(line => line.type === "LABOR").reduce((sum, line) => sum + Number(line.lineTotal), 0) ?? 0;
  const materialTotal = invoice
    ? invoice.lines.filter(line => line.type === "PART").reduce((sum, line) => sum + Number(line.lineTotal), 0)
    : estimate?.lines.filter(line => line.type === "PART").reduce((sum, line) => sum + Number(line.lineTotal), 0) ?? 0;
  const [otherCharges, setOtherCharges] = useState(Number(invoice?.shopSupplyAmount ?? estimate?.shopSupplyAmount ?? 0));
  const [discount, setDiscount] = useState(Number(invoice?.discountAmount ?? estimate?.discountAmount ?? 0));
  const tax = Number(invoice?.taxAmount ?? estimate?.taxAmount ?? materialTotal * 0.0975);
  const beforeDiscount = laborTotal + materialTotal + otherCharges + tax;
  const total = Math.max(beforeDiscount - discount, 0);
  const save = useWorkflowMutation<void>(() => repairOrdersApi.updateEstimateAdjustments(id, {
    shopSupplyAmount: otherCharges,
    discountAmount: discount
  }), refresh);

  useEffect(() => {
    setOtherCharges(Number(invoice?.shopSupplyAmount ?? estimate?.shopSupplyAmount ?? 0));
    setDiscount(Number(invoice?.discountAmount ?? estimate?.discountAmount ?? 0));
  }, [estimate?.shopSupplyAmount, estimate?.discountAmount, invoice?.shopSupplyAmount, invoice?.discountAmount]);

  return <Card
    className="settlement-summary-card"
    title={text.settlementBreakdown}
    extra={editable && <Button type="primary" loading={save.isPending} onClick={() => save.mutate()}>
      {text.save}
    </Button>}
  >
    {save.isError && <MutationError error={save.error} />}
    <Descriptions column={1} size="small" items={[
      { key: "labor", label: text.laborCost, children: formatCurrency(laborTotal) },
      { key: "materials", label: text.materialCost, children: formatCurrency(materialTotal) },
      {
        key: "other",
        label: text.otherCharges,
        children: editable
          ? <InputNumber
              min={0}
              precision={2}
              value={otherCharges}
              formatter={formatGroupedInput}
              parser={parseGroupedInput}
              onChange={value => setOtherCharges(value ?? 0)}
              addonBefore="$"
            />
          : formatCurrency(otherCharges)
      },
      {
        key: "discount",
        label: text.discount,
        children: editable
          ? <InputNumber
              min={0}
              max={beforeDiscount}
              precision={2}
              value={discount}
              formatter={formatGroupedInput}
              parser={parseGroupedInput}
              onChange={value => setDiscount(Math.min(value ?? 0, beforeDiscount))}
              addonBefore="$"
            />
          : formatCurrency(discount)
      },
      { key: "tax", label: text.losAngelesSalesTax, children: formatCurrency(tax) },
      { key: "total", label: text.grandTotal, children: <b>{formatCurrency(total)}</b> }
    ]} />
    {invoiceEditable && <Typography.Text type="secondary">{text.financialAdjustmentsBeforeCollection}</Typography.Text>}
    {!editable && (estimate || invoice) && <Typography.Text type="secondary">{text.financialAdjustmentsLocked}</Typography.Text>}
  </Card>;
}

export function InvoiceSection({ data, text }: Omit<Props, "id" | "refresh">) {
  const invoice = data.repairOrder.invoice;
  if (!invoice) return <Card><Empty description={text.invoiceCreatedAtReadyDelivery} /></Card>;
  return <Card title={`${text.invoice} ${invoice.invoiceNumber}`} extra={<Tag color={invoice.status === "PAID" ? "green" : "blue"}>{invoice.status.replaceAll("_", " ")}</Tag>}>
    <Table rowKey="id" pagination={false} dataSource={invoice.lines} columns={[
      { title: text.description, dataIndex: "description" },
      { title: text.type, dataIndex: "type" },
      { title: text.quantity, dataIndex: "quantity", align: "right" as const },
      { title: text.unitPrice, dataIndex: "unitPrice", align: "right" as const, render: formatCurrency },
      {
        title: text.discount,
        key: "discount",
        align: "right" as const,
        render: (_: unknown, line: NonNullable<WorkflowData["repairOrder"]["invoice"]>["lines"][number]) =>
          line.type === "LABOR"
            ? formatCurrency(Math.max(Number(line.quantity) * Number(line.unitPrice) - Number(line.lineTotal), 0))
            : "—"
      },
      { title: text.total, dataIndex: "lineTotal", align: "right" as const, render: formatCurrency }
    ]} />
  </Card>;
}

export function PaymentSection({ id, data, text, refresh }: Props) {
  const invoice = data.repairOrder.invoice;
  const [amount, setAmount] = useState(() => Number(invoice?.balance ?? 0));
  const [method, setMethod] = useState("CASH");
  const [reference, setReference] = useState("");
  const payment = useWorkflowMutation<void>(() => repairOrdersApi.recordPayment(id, { amount, method, reference }), refresh);
  useEffect(() => setAmount(Number(invoice?.balance ?? 0)), [invoice?.balance]);
  if (!invoice) return <Card><Empty description={text.invoiceRequiredBeforePayment} /></Card>;
  return <div className="workflow-section-stack">
    {payment.isError && <MutationError error={payment.error} />}
    <Card title={text.recordPayment}><form onSubmit={event => { event.preventDefault(); if (amount > 0 && Number(invoice.balance) > 0) payment.mutate(); }}>
      <Space wrap className="workflow-inline-form">
        <InputNumber min={0.01} max={Number(invoice.balance)} precision={2} value={amount} formatter={formatGroupedInput} parser={parseGroupedInput} onChange={value => setAmount(value ?? 0)} addonBefore="$" />
        <Select value={method} onChange={setMethod} options={["CASH", "TRANSFER", "CHECK", "PAYPAL", "ZELLE"].map(value => ({ value, label: text[`paymentMethod${value}`] ?? value }))} />
        <Input value={reference} onChange={event => setReference(event.target.value)} placeholder={text.reference} />
        <Button type="primary" htmlType="submit" disabled={amount <= 0 || Number(invoice.balance) <= 0} loading={payment.isPending}>{text.payment}</Button>
      </Space>
    </form></Card>
    <Card title={text.paymentHistory}><List dataSource={invoice.payments} locale={{ emptyText: text.noData }} renderItem={item => <List.Item><List.Item.Meta title={`${formatCurrency(item.amount)} · ${text[`paymentMethod${item.method}`] ?? item.method}`} description={`${item.reference || "—"} · ${item.receivedBy?.displayName ?? text.system} · ${formatDateTime(item.receivedAt)}`} /></List.Item>} /></Card>
  </div>;
}

export function BusinessOperationsSection({ id, data, text, refresh }: Props) {
  const order = data.repairOrder;
  const t = (key: string) => text[key] ?? key;
  const [channel, setChannel] = useState<"SMS" | "EMAIL" | "PHONE" | "IN_PERSON">("PHONE");
  const [purpose, setPurpose] = useState<"READY_FOR_PICKUP" | "INVOICE" | "PAYMENT_RECEIPT" | "FOLLOW_UP" | "WARRANTY">("READY_FOR_PICKUP");
  const [recipient, setRecipient] = useState(order.customer.phone || order.customer.email || "");
  const [communicationMessage, setCommunicationMessage] = useState("");
  const [recipientName, setRecipientName] = useState(customerName(order.customer));
  const [mileageOut, setMileageOut] = useState<number | null>(
    order.mileageOut ?? order.mileageIn ?? order.vehicle.mileage ?? null
  );
  const [deliveryNotes, setDeliveryNotes] = useState("");
  const communication = useWorkflowMutation<void>(() => repairOrdersApi.recordCustomerCommunication(id, {
    channel,
    purpose,
    recipient,
    message: communicationMessage
  }), () => {
    setCommunicationMessage("");
    refresh();
  });
  const delivery = useWorkflowMutation<void>(() => repairOrdersApi.deliverVehicle(id, {
    recipientName,
    ...(mileageOut !== null ? { mileageOut } : {}),
    notes: deliveryNotes
  }), refresh);
  const warranties = order.services.flatMap(service => service.warrantyRecord
    ? [{ ...service.warrantyRecord, serviceName: service.serviceCatalog.serviceName }]
    : []);
  const completedSteps = [
    Boolean(order.invoice),
    order.invoice?.status === "PAID",
    Boolean(order.delivery),
    order.serviceHistory.length > 0 || Boolean(order.delivery)
  ].filter(Boolean).length;

  useEffect(() => {
    if (channel === "EMAIL") setRecipient(order.customer.email || "");
    else if (channel === "SMS" || channel === "PHONE") setRecipient(order.customer.phone || "");
    else setRecipient(customerName(order.customer));
  }, [channel, order.customer]);

  return <div className="business-operations">
    <Card className="business-flow-card">
      <Steps
        responsive
        current={Math.max(0, completedSteps - 1)}
        items={[
          { title: t("invoiceIssued"), status: order.invoice ? "finish" : "wait" },
          { title: t("paymentComplete"), status: order.invoice?.status === "PAID" ? "finish" : order.invoice ? "process" : "wait" },
          { title: t("vehicleDelivered"), status: order.delivery ? "finish" : order.status === "PAID" ? "process" : "wait" },
          { title: t("historyCompleted"), status: order.delivery ? "finish" : "wait" }
        ]}
      />
    </Card>

    <div className="business-operations-grid">
      <Card title={t("customerCommunication")} extra={<SendOutlined />}>
        {(communication.isError) && <MutationError error={communication.error} />}
        <form className="business-form" onSubmit={event => {
          event.preventDefault();
          if (communicationMessage.trim()) communication.mutate();
        }}>
          <div className="business-form-row">
            <Select
              value={channel}
              onChange={setChannel}
              options={["SMS", "EMAIL", "PHONE", "IN_PERSON"].map(value => ({
                value, label: t(`communicationChannel${value}`)
              }))}
            />
            <Select
              value={purpose}
              onChange={setPurpose}
              options={["READY_FOR_PICKUP", "INVOICE", "PAYMENT_RECEIPT", "FOLLOW_UP", "WARRANTY"].map(value => ({
                value, label: t(`communicationPurpose${value}`)
              }))}
            />
          </div>
          <Input value={recipient} onChange={event => setRecipient(event.target.value)} placeholder={t("recipient")} />
          <Input.TextArea
            value={communicationMessage}
            onChange={event => setCommunicationMessage(event.target.value)}
            placeholder={t("communicationMessage")}
            autoSize={{ minRows: 2, maxRows: 5 }}
          />
          <Button type="primary" htmlType="submit" icon={<SendOutlined />} loading={communication.isPending} disabled={!communicationMessage.trim()}>
            {t("recordCommunication")}
          </Button>
        </form>
        <List
          className="communication-history"
          dataSource={order.communications}
          locale={{ emptyText: t("noCustomerCommunications") }}
          renderItem={item => <List.Item>
            <List.Item.Meta
              title={<Space><Tag>{t(`communicationChannel${item.channel}`)}</Tag><b>{t(`communicationPurpose${item.purpose}`)}</b></Space>}
              description={<>{item.message}<div className="table-secondary">{item.recipient || "—"} · {item.actor?.displayName ?? t("system")} · {formatDateTime(item.occurredAt)}</div></>}
            />
          </List.Item>}
        />
      </Card>

      <Card title={t("vehicleDelivery")} extra={<CheckCircleOutlined />}>
        {delivery.isError && <MutationError error={delivery.error} />}
        {order.delivery ? <Descriptions column={1} size="small" items={[
          { key: "recipient", label: t("deliveryRecipient"), children: order.delivery.recipientName },
          { key: "mileage", label: t("mileageOut"), children: formatMileage(order.delivery.mileageOut) },
          { key: "released", label: t("releasedAt"), children: formatDateTime(order.delivery.releasedAt) },
          { key: "by", label: t("releasedBy"), children: order.delivery.releasedBy?.displayName ?? t("system") },
          { key: "notes", label: t("deliveryNotes"), children: order.delivery.notes || "—" }
        ]} /> : <form className="business-form" onSubmit={event => {
          event.preventDefault();
          if (order.status === "PAID" && recipientName.trim()) delivery.mutate();
        }}>
          {order.status !== "PAID" && <Alert type="info" showIcon message={t("deliveryRequiresPayment")} />}
          <Input value={recipientName} onChange={event => setRecipientName(event.target.value)} placeholder={t("deliveryRecipient")} />
          <InputNumber
            min={0}
            precision={0}
            value={mileageOut}
            formatter={formatGroupedInput}
            parser={parseGroupedInput}
            onChange={value => setMileageOut(value)}
            addonBefore={t("mileageOut")}
          />
          <Input.TextArea value={deliveryNotes} onChange={event => setDeliveryNotes(event.target.value)} placeholder={t("deliveryNotes")} autoSize={{ minRows: 2, maxRows: 4 }} />
          <Button type="primary" htmlType="submit" icon={<CheckCircleOutlined />} loading={delivery.isPending} disabled={order.status !== "PAID" || !recipientName.trim()}>
            {t("deliverVehicleAndCompleteHistory")}
          </Button>
        </form>}
      </Card>

      <Card title={t("warrantyCoverage")} extra={<SafetyCertificateOutlined />}>
        <List
          dataSource={warranties}
          locale={{ emptyText: t("noWarrantyCoverage") }}
          renderItem={item => <List.Item>
            <List.Item.Meta
              title={item.serviceName}
              description={<Space direction="vertical" size={2}>
                <span>{t("laborWarrantyEnds")}: {item.laborEndsAt ? formatDate(item.laborEndsAt) : "—"}</span>
                <span>{t("partsWarrantyEnds")}: {item.partsEndsAt ? formatDate(item.partsEndsAt) : "—"}</span>
                {item.terms && <small>{item.terms}</small>}
              </Space>}
            />
          </List.Item>}
        />
      </Card>

      <Card title={t("permanentRepairHistory")} extra={<FileSearchOutlined />}>
        <List
          dataSource={order.serviceHistory}
          locale={{ emptyText: t("historyCreatedAtDelivery") }}
          renderItem={item => <List.Item>
            <List.Item.Meta
              title={`${item.serviceCode} · ${item.serviceName}`}
              description={`${formatDate(item.performedAt)} · ${formatMileage(item.mileage)} · ${formatCurrency(Number(item.laborAmount) + Number(item.partsAmount))}`}
            />
          </List.Item>}
        />
      </Card>
    </div>
  </div>;
}

function nextAction(status: RepairOrderStatus, data: WorkflowData, text: TextMap) {
  const t = (key: string) => text[key] ?? key;
  const map: Partial<Record<RepairOrderStatus, { key: string; label: string; description: string }>> = {
    DRAFT: { key: "check-in", label: t("checkInVehicle"), description: t("checkInDescription") },
    APPROVED: { key: "start-work", label: t("startRepairWork"), description: t("startWorkDescription") },
    IN_PROGRESS: data.progress.laborCompleted && data.progress.partsIssued
      ? { key: "quality", label: t("beginQualityCheck"), description: t("qualityDescription") }
      : undefined,
    QUALITY_CHECK: { key: "ready", label: t("passQcIssueInvoice"), description: t("readyDescription") },
    PAID: { key: "close", label: t("deliverVehicle"), description: t("deliveryRequiresPayment") }
  };
  return map[status] ?? null;
}

function workflowStep(status: RepairOrderStatus) {
  const order: RepairOrderStatus[] = ["DRAFT", "CHECKED_IN", "DIAGNOSIS", "ESTIMATE", "WAITING_APPROVAL", "APPROVED", "IN_PROGRESS", "SUPPLEMENT_REQUIRED", "QUALITY_CHECK", "READY_DELIVERY", "PAID", "CLOSED"];
  return Math.max(0, Math.min(7, Math.round((order.indexOf(status) / (order.length - 1)) * 7)));
}

function FindingTag({ condition, text }: { condition: "PASS" | "ATTENTION" | "FAIL"; text: TextMap }) {
  return <Tag color={condition === "PASS" ? "green" : condition === "ATTENTION" ? "orange" : "red"}>{text[condition.toLowerCase()]}</Tag>;
}

function serviceTotal(service: RepairOrderServiceItem) {
  return Number(service.laborAmount)
    + service.parts.reduce((sum, part) => sum + Number(part.amount), 0);
}

function serviceTimeline(data: WorkflowData, serviceId: string) {
  return data.repairOrder.timeline.filter(item => {
    const metadata = item.metadata as Record<string, unknown> | null;
    if (!metadata) return false;
    return metadata.repairOrderServiceId === serviceId
      || metadata.serviceId === serviceId
      || (metadata.data as Record<string, unknown> | undefined)?.repairOrderServiceId === serviceId;
  });
}
function LaborStatusTag({ status }: { status: string }) {
  return <Tag color={status === "COMPLETED" ? "green" : status === "IN_PROGRESS" ? "blue" : "default"}>{status.replaceAll("_", " ")}</Tag>;
}
function EstimateStatus({ status }: { status?: string }) {
  return status ? <Tag color={status === "APPROVED" ? "green" : status === "WAITING_APPROVAL" ? "gold" : "blue"}>{status.replaceAll("_", " ")}</Tag> : null;
}
function SummaryTile({ label, value }: { label: string; value: string }) {
  return <Card size="small" className="workflow-summary-tile"><span>{label}</span><b>{value}</b></Card>;
}

function formatWorkDuration(seconds: number) {
  const safe = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const remainder = safe % 60;
  return [hours, minutes, remainder].map(value => String(value).padStart(2, "0")).join(":");
}

function ServiceWorkTimer({ session }: { session: NonNullable<RepairOrderServiceItem["workSession"]> }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (session.status !== "RUNNING") return;
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [session.status]);
  const seconds = session.status === "RUNNING" && session.runningSince
    ? session.accumulatedSeconds
      + Math.max(0, Math.floor((now - new Date(session.runningSince).getTime()) / 1000))
    : session.accumulatedSeconds;
  return <>{formatWorkDuration(seconds)}</>;
}
function MutationError({ error }: { error: Error | null }) {
  const message = (error as { response?: { data?: { message?: string } } } | null)?.response?.data?.message ?? error?.message ?? "Workflow operation failed";
  return <Alert type="error" showIcon message={message} />;
}
